import { Alert, Table, Tag, Tooltip, Typography } from "antd";
import GoldenNominalBadge from "components/atoms/GoldenNominalBadge";
import { comparisonRows, memberState } from "common/reviewQueue";
import React from "react";

interface IReviewItemComparisonProps {
    item: IReviewQueueItem;
    // Member records by dataObjectId: the record (PII masked), null when gone, or { readError }.
    records: { [dataObjectId: string]: any };
    fields: { fieldId: string; name?: string; isPII?: boolean }[];
}

const MEMBER_STATE_TEXT: { [state: string]: string } = {
    missing: "No longer exists",
    unreadable: "Could not be read",
    loading: "Loading…",
};

// The members of a review item side by side: one column per record, one row per schema field.
// Fields whose values differ are tinted; the fields the steward must look at are marked. Personal
// data is masked by the service, so those fields are labelled and not compared.
const ReviewItemComparison = ({ item, records, fields }: IReviewItemComparisonProps) => {
    const visibleMembers = item.members.filter((member) => !member.redacted);
    const redactedCount = item.members.length - visibleMembers.length;
    const rows = comparisonRows(fields, item, records);
    const maskedCount = rows.filter((row) => row.masked).length;

    const columns = [
        {
            title: "Field",
            dataIndex: "name",
            key: "field",
            fixed: "left" as const,
            width: 200,
            render: (name: string, row: ReturnType<typeof comparisonRows>[number]) => (
                <span>
                    {name} {row.conflict && <Tag color="volcano">Check</Tag>}
                    {row.masked && (
                        <Tooltip title="Personal data is masked, so its values are not shown or compared.">
                            <Tag>Personal data</Tag>
                        </Tooltip>
                    )}
                </span>
            ),
        },
        ...visibleMembers.map((member, index) => {
            const state = memberState(member, records);
            return {
                key: member.dataObjectId || String(index),
                title: (
                    <div data-testid="review-item-member-heading">
                        <Typography.Text strong copyable={{ text: member.dataObjectId }}>
                            {member.dataObjectId === item.proposedSurvivorId ? "Survivor" : `Record ${index + 1}`}
                        </Typography.Text>
                        {member.isGolden && <GoldenNominalBadge className="ml-2" />}
                        <div className="text-xs text-gray-500">
                            Dataset {member.dataSetId} · version {member.version ?? "–"}
                        </div>
                        {state !== "loaded" && (
                            <Tooltip title={state === "unreadable" ? records[member.dataObjectId as string]?.readError : undefined}>
                                <Tag color={state === "loading" ? "default" : "red"}>{MEMBER_STATE_TEXT[state]}</Tag>
                            </Tooltip>
                        )}
                    </div>
                ),
                render: (_: unknown, row: ReturnType<typeof comparisonRows>[number]) => row.values[index],
            };
        }),
    ];

    return (
        <>
            {redactedCount > 0 && (
                <Alert
                    className="!mb-3"
                    type="info"
                    showIcon
                    title={`${redactedCount} record${redactedCount > 1 ? "s are" : " is"} in a business area you cannot see, and not shown.`}
                />
            )}
            {maskedCount > 0 && (
                <Alert
                    className="!mb-3"
                    type="info"
                    showIcon
                    title={`${maskedCount} personal-data field${maskedCount > 1 ? "s are" : " is"} masked, so ${maskedCount > 1 ? "their" : "its"} values are not compared here.`}
                />
            )}
            <Table
                size="small"
                rowKey="fieldId"
                pagination={false}
                scroll={{ x: "max-content" }}
                dataSource={rows}
                columns={columns}
                rowClassName={(row) => (row.differs ? "bg-amber-50" : "")}
                data-testid="review-item-comparison"
            />
        </>
    );
};

export default ReviewItemComparison;
